import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as Slot from "radix-ui/slot";
import { forwardRef, useLayoutEffect, useRef, useState, type ButtonHTMLAttributes } from "react";
import { cn } from "./cn";

export const buttonVariants = cva(
  "inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-md border font-medium transition-colors duration-120 ease-out disabled:pointer-events-none aria-busy:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "border-accent bg-accent text-on-accent hover:border-accent-hover hover:bg-accent-hover",
        secondary: "border-border-strong bg-surface text-fg hover:bg-surface-hover",
        ghost: "border-transparent bg-transparent text-fg-muted hover:bg-surface-hover hover:text-fg",
        /**
         * Redesign 2026-09: a solid danger fill belongs to a DENIED result and to the confirm step of
         * a destructive dialog — nowhere else. A destructive button standing in a page is a soft
         * `danger` outline; this fill is `ConfirmDialog`'s.
         */
        destructive: "border-danger bg-danger text-on-accent hover:opacity-90",
      },
      size: {
        sm: "h-(--btn-h-dense) px-2.5 text-cell",
        md: "h-(--btn-h) px-3 text-body",
        icon: "size-(--btn-h) p-0 text-body",
      },
    },
    defaultVariants: { variant: "secondary", size: "md" },
  },
);

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Shows a spinner, sets aria-busy and blocks clicks while an action is in flight. */
  loading?: boolean;
  /**
   * What the button is doing, shown INSTEAD of dropping the label. A button that becomes a bare
   * spinner stops saying what it was going to do at the moment the reader most wants to know.
   */
  loadingLabel?: string;
  /**
   * Why this is disabled — a permission, a plan limit, a terminal that is offline. Rendered as the
   * accessible description and the tooltip, because a control that is merely dimmed teaches nothing:
   * the reader cannot tell "you may not" from "not yet" from "broken".
   */
  disabledReason?: string;
  /** Render the child element instead of a <button> (links styled as buttons). */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    loading = false,
    loadingLabel,
    disabledReason,
    asChild = false,
    disabled,
    children,
    type,
    style,
    ...props
  },
  ref,
) {
  const Comp = asChild ? Slot.Root : "button";
  /**
   * The width the button had before it started working, held for as long as it is working. Without
   * this the spinner widens the button and everything beside it steps sideways — on a toolbar, on a
   * row, in a dialog footer — which reads as the page breaking rather than as work happening.
   */
  const node = useRef<HTMLButtonElement | null>(null);
  const [lockedWidth, setLockedWidth] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!loading) {
      setLockedWidth(null);
      return;
    }
    const width = node.current?.getBoundingClientRect().width;
    if (width) setLockedWidth((held) => held ?? Math.ceil(width));
  }, [loading]);

  const isDisabled = disabled || loading;
  return (
    <Comp
      ref={(el: HTMLButtonElement | null) => {
        node.current = el;
        if (typeof ref === "function") ref(el);
        else if (ref) ref.current = el;
      }}
      type={asChild ? undefined : (type ?? "button")}
      className={cn(
        buttonVariants({ variant, size }),
        // Disabled with a stated reason keeps its ink: it is a control the reader may not use yet,
        // not a control that has gone away. Only an unexplained disable fades.
        isDisabled && (disabledReason ? "cursor-not-allowed opacity-70" : "opacity-50"),
        className,
      )}
      style={lockedWidth === null ? style : { ...style, minWidth: lockedWidth }}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      aria-description={disabledReason && isDisabled ? disabledReason : undefined}
      title={disabledReason && isDisabled ? disabledReason : props.title}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading ? <Loader2 size={16} strokeWidth={1.5} className="animate-spin" aria-hidden /> : null}
          {loading && loadingLabel ? loadingLabel : children}
        </>
      )}
    </Comp>
  );
});
